/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { NotificationTypeV1 } from "@startree-ui/platform-ui";
import { ReactElement, ReactNode } from "react";
import { OutputSchema } from "../../store/dataset-ingestion/dataset-ingestion.interfaces";
import { IngestionFormats } from "./datasets.interfaces";

// enums

// Basic enums
export enum ConnectionTypes {
    AWS_MSK = "AWS_MSK",
    BIG_QUERY = "BIG_QUERY",
    DELTA_LAKE = "DELTA_LAKE",
    GCS = "GCS", // Google Cloud Storage
    KAFKA = "KAFKA",
    KINESIS = "KINESIS",
    PINOT = "PINOT",
    PRESTO = "PRESTO",
    S3 = "S3",
    SAMPLE = "SAMPLE",
    SNOWFLAKE = "SNOWFLAKE",
    THIRDEYE = "THIRDEYE",
    UPLOAD = "UPLOAD",
    CONFLUENT_CLOUD = "CONFLUENT_CLOUD",
    UNKNOWN = "UNKNOWN",
    CUSTOM = "CUSTOM",
}

export enum CategoryTypes {
    BATCH = "BATCH",
    SQL = "SQL",
    STREAMING = "STREAMING",
    UPLOAD = "UPLOAD",
    SAMPLE = "SAMPLE",
    CUSTOM = "CUSTOM",
}

// Connection Config and Ingestion Properties
export enum BigQueryConnectionConfig {
    DATASET_NAMES = "sql.bigquery.datasetnames",
    PROJECT_ID = "sql.bigquery.projectid",
    JSON_KEY = "sql.bigquery.jsonkey",
}

export enum CommonConnectionConfig {
    AUTHENTICATION_TYPE = "authentication.type",
    SRN = "srn",
}

export enum CommonIngestionProperties {
    RECORD_READER_CONFIG = "recordReaderConfig",
}

export enum ConfluentCloudConnectionConfig {
    BROKER_LIST = "stream.kafka.broker.list",
    API_KEY = "stream.confluent.key",
    API_SECRET = "stream.confluent.secret",
    SCHEMA_REGISTRY = "stream.kafka.decoder.prop.schema.registry.rest.url",
    SCHEMA_REGISTRY_API_KEY = "stream.confluent.schema.registry.key",
    SCHEMA_REGISTRY_API_SECRET = "stream.confluent.schema.registry.secret",
}

export enum DeltaLakeConnectionConfig {
    ACCESS_KEY = "batch.s3.accessKey",
    FS_TYPE = "delta.fs.type",
    TABLE_URI = "delta.table.uri",
    REGION = "batch.s3.region",
    SECRET_KEY = "batch.s3.secretKey",
}

export enum DeltaLakeIngestionProperties {
    TABLE_URI = "delta.table.uri",
}

export enum KafkaConnectionConfig {
    BROKER_LIST = "stream.kafka.broker.list",
    SECURITY_PROTOCOL = "security.protocol",
    PASSWORD = "stream.kafka.password",
    REGISTRY_PASSWORD = "stream.kafka.decoder.prop.schema.registry.secret",
    REGISTRY_USERNAME = "stream.kafka.decoder.prop.schema.registry.key",
    SCHEMA = "stream.kafka.decoder.prop.schema",
    SCHEMA_REGISTRY = "stream.kafka.decoder.prop.schema.registry.rest.url",
    USERNAME = "stream.kafka.username",
    SERVER_CERTIFICATE = "stream.kafka.ssl.server.certificate",
    CLIENT_CERTIFICATE = "stream.kafka.ssl.client.certificate",
    CLIENT_KEY = "stream.kafka.ssl.client.key",
}

export enum KafkaIngestionProperties {
    DATA_FORMAT = "stream.kafka.dataFormat",
    TOPIC_NAME = "stream.kafka.topicName",
}

export enum KinesisConnectionConfig {
    ACCESS_KEY = "stream.kinesis.accessKey",
    REGION = "stream.kinesis.region",
    SECRET_KEY = "stream.kinesis.secretKey",
}

export enum KinesisIngestionProperties {
    DATA_FORMAT = "stream.kinesis.dataFormat",
    STREAM_NAME = "streamName",
}

export enum S3ConnectionConfig {
    ACCESS_KEY = "batch.s3.accessKey",
    BUCKET = "batch.s3.bucket",
    PREFIX = "batch.s3.prefix",
    REGION = "batch.s3.region",
    SECRET_KEY = "batch.s3.secretKey",

    START_AFTER = "batch.s3.startAfter",
    MAX_COUNT = "batch.s3.maxCount",
}

export enum IAMRoleConfig {
    ACCOUNT_ID = "aws.accountId",
    EXTERNAL_ID = "aws.externalId",
    IAM_ROLE_ARN = "aws.iamRoleArn",
}

export enum S3IngestionProperties {
    DATA_FORMAT = "batch.dataFormat",
    EXCLUDE_PATTERN_MATCH = "batch.exclude.file.pattern.match",
    INCLUDE_PATTERN_MATCH = "batch.include.file.pattern.match",
    INPUT_PATH = "batch.inputPath",
    INPUT_PATHS = "batch.inputPaths",
    PREVIEW_OBJECTS = "batch.previewObjects",

    START_AFTER = "batch.s3.startAfter",
    MAX_COUNT = "batch.s3.maxCount",
}

export enum SnowflakeConnectionConfig {
    ACCOUNT = "sql.snowflake.account",
    DATABASE = "sql.snowflake.db",
    PASSWORD = "sql.snowflake.password",
    SCHEMA = "sql.snowflake.schema",
    USER = "sql.snowflake.user",
}

export enum SqlIngestionProperties {
    QUERY = "sql.query",
    TABLE_SELECTOR = "sql.tableSelector",
}

export enum SqlIngestionSettings {
    START_TIME = "sql.startTime",
    TIME_COLUMN_FORMAT = "sql.timeColumnFormat",
    TIME_COLUMN_NAME = "sql.timeColumnName",
    NUM_BUCKETS_TO_LOOKBACK = "sql.numBucketsToLookBack",
}

export enum UploadIngestionProperties {
    UPLOAD_PAYLOAD_BASE64 = "upload.payloadBase64",
}

// types

// The payload for a create connection API call
export interface EditableConnection {
    connection: ConnectionConfig;
    name: string;
    namespaceSrn?: string;
    type?: ConnectionTypes;
}

export interface Connection {
    clusterSrn: string | null;
    connection: ConnectionConfig;
    createdAt?: number;
    description: string | null;
    external?: boolean;
    generated: boolean;
    lastUpdated: number; // should be DateTime
    name: string;
    namespaceSrn?: string;
    srn: string;
    type?: ConnectionTypes;
}

export type ConnectionConfig = Partial<
    ConnectionConfigCommon &
        ConnectionConfigBigQuery &
        ConnectionConfigConfluentCloud &
        ConnectionConfigDeltaLake &
        ConnectionConfigKafka &
        ConnectionConfigKinesis &
        ConnectionConfigPinot &
        ConnectionConfigS3 &
        ConnectionConfigSnowflake
>;

/**
 * The naming used here is somewhat confusing, so here's an explanation:
 *
 * # Currently
 * - <Connector>ConnectionConfig refers to the enum with the keys
 * - ConnectionConfig<Connector> refers to the record with the above enum as keys, and string values
 * For Connector = BigQuery | Kafka | ConfluentCloud | Kinesis | S3 | DeltaLake | etc...
 *
 * # In the Future
 * These should be renamed to be more self explanatory, say like:
 * - <Connector>ConnectionConfig -> <Connector>ConnectionConfigKeys [as Enum]
 * - ConnectionConfig<Connector> -> <Connector>ConnectionConfig [as Record<^Enum, string>]
 */

export type ConnectionConfigCommon = Record<CommonConnectionConfig, string>;

export type ConnectionConfigBigQuery = Record<BigQueryConnectionConfig, string>;

export type ConnectionConfigConfluentCloud = Record<
    ConfluentCloudConnectionConfig,
    string
>;

export type ConnectionConfigKafka = Record<KafkaConnectionConfig, string>;

export type ConnectionConfigKinesis = Record<KinesisConnectionConfig, string>;

export interface ConnectionConfigPinot {
    brokerExternalUrl: string;
    brokerUrl: string;
    controllerExternalUrl: string;
    controllerUrl: string;
    frontendExternalUrl: string;
    proxyUrl: string;
    zookeeperUrl: string;
}

export type ConnectionConfigS3 = Record<S3ConnectionConfig, string>;

export type ConnectionConfigDeltaLake = Record<
    DeltaLakeConnectionConfig,
    string
>;

export type ConnectionConfigSnowflake = Record<
    SnowflakeConnectionConfig,
    string
>;

export interface ConnectionProperties {
    connectionMethod: string;
    dataSourceType: ConnectionTypes | "";
    properties: InputProperty[];
}

export interface DataSourceCategory {
    category: CategoryTypes;
    description: string;
}

interface DataSourceTypesType {
    description?: string;
    category: CategoryTypes;
    name: string;
    type: ConnectionTypes;
}

export type DataSourceTypesResponseType = DataSourceTypesType[];

export interface ExploreDataSourcesArgsType {
    ingestionConnection: ConnectionConfig;
    ingestionType: ConnectionTypes;
}

export interface ExploreDataSourcesResponseType {
    locations: Record<"name", string>[];
}

interface InferArgsType {
    ingestionConnection?: ConnectionConfig;
    ingestionFormat: IngestionFormats;
    ingestionLocator?: string | IngestionLocator;
    ingestionType: ConnectionTypes;
}

export interface InferConfigArgsType extends InferArgsType {
    dataSetName?: string;
    datasetProperties?: {
        "kafka.upsert.primaryKeyColumns.group": {
            "kafka.upsert.primaryKeyColumns": string; // Comma joined string
        };
    };
    ingestionLocator?: IngestionLocator;
    outputSchema: SchemaResponseType;
    timeColumn?: string;
    isDimensionTable: boolean;
}

export interface InferConfigResponseType {
    config: string;
    description: string;
    type: string;
}

export interface InferSchemaArgsType extends InferArgsType {
    payloadBase64?: string;
}

export interface InferSchemaFromSampleArgsType {
    schema: SchemaResponseType;
    records: Record<string, unknown>;
}

export interface InferSchemaFromSampleResponseType {
    inputSchema: SchemaResponseType;
    outputSchema: SchemaResponseType;
}

export interface InferSchemaResponseType {
    inputSchema: SchemaResponseType;
    outputSchema: SchemaResponseType | null;
}

export type IngestionLocator = Partial<IngestionLocatorCommon> &
    Partial<IngestionLocatorDeltaLake> &
    Partial<IngestionLocatorKafka> &
    Partial<IngestionLocatorKinesis> &
    Partial<IngestionLocatorS3> &
    Partial<IngestionLocatorSql> &
    Partial<IngestionLocatorUpload>;

export type IngestionLocatorCommon = {
    [key in CommonIngestionProperties]: string;
};

export type IngestionLocatorDeltaLake = Record<
    DeltaLakeIngestionProperties,
    string
>;

export type IngestionLocatorKafka = Record<KafkaIngestionProperties, string>;

export type IngestionLocatorKinesis = Record<
    KinesisIngestionProperties,
    string
>;

export type IngestionLocatorS3 = Record<S3IngestionProperties, string>;

// BigQuery/Snowflake
export type IngestionLocatorSql = {
    locatorMethod?: string;
    locator?: string;
} & Record<SqlIngestionProperties, string> &
    Record<SqlIngestionSettings, string>;

export type IngestionLocatorUpload = Record<UploadIngestionProperties, string>;

export interface IngestionProperties {
    dataSourceType: ConnectionTypes | "";
    ingestionMethod: string;
    properties: InputProperty[];
}

export interface InputProperty {
    defaultValue?: string;
    description?: ReactNode | string[] | string | null; // TODO: clarify with backend
    disabled?: boolean;
    key: string;
    readOnly: boolean;
    required: boolean;
    secret: boolean;
    tooltip?: boolean;
    value: string | string[];
    footerComponent?: ReactNode;
    showAsLabel?: boolean;
    order?: number;
}

export interface Issues {
    description: string;
    severity: NotificationTypeV1;
}

export interface PreviewIngestionArgsType extends InferArgsType {
    dataSetName?: string;
    inputRecords?: PreviewIngestionResponseType;
    outputSchema?: SchemaResponseType;
    payloadBase64?: string;
    timeColumn?: string;
}

export interface PreviewIngestionResponseType {
    columnNames: string[];
    records: Array<Array<string | ReactElement | Record<string, unknown>>>;
    previewObjects?: string[] | null;
}

export interface RunIngestionArgsType {
    dataSourceSrn?: string;
    datasetName: string;
    ingestionFormat?: IngestionFormats;
    ingestionType: ConnectionTypes;
    payloadBase64?: string;
}

export interface RunIngestionResponseType {
    messages: string[];
    success: boolean;
}

export interface SchemaResponseType {
    description?: string;
    schema: string;
    type: string;
}

export interface TestConnectionArgsType {
    ingestionConnection?: ConnectionConfig;
    ingestionType: ConnectionTypes;
}

export interface TestConnectionResponseType {
    messages: string[];
    pass: boolean;
}

export interface TenantsResponseType {
    typeToTenantsMap: {
        BROKER_TENANTS: string[];
        SERVER_TENANTS: string[];
    };
}

export interface ValidateConfigArgsType {
    configStr: string;
    dataSetType: string;
    dataSourceSrn: string;
}

export interface ValidateResponseType {
    issues: Issues[];
    pass: boolean;
}

export interface ValidateSchemaArgsType {
    dataSourceSrn: string;
    schema: SchemaResponseType;
    dataSetName: string;
}

export type PreviewTableArgsType = {
    tableDto: {
        dataSource: {
            srn?: string;
        };
        datasourceProperties: {
            "batch.inputPaths"?: string;
            recordReaderConfig?: string;
            "stream.kafka.topicName"?: string;
            "batch.include.file.pattern.match"?: string;
            "batch.exclude.file.pattern.match"?: string;
        };
        dataFormat: string | null;
        pinotSchema?: OutputSchema["schema"]["schema"];
    };
    config?: {
        sampling?: {
            min: number;
            max: number;
        };
        inference?: {
            schemaInferenceEnabled: boolean;
        };
        previewFiles?: {
            previewFilesOnly: boolean;
        };
    };
    dataManagerPreviewConfig?: {
        columns: {
            sourceColumnsToShow: (string | undefined)[];
            previewColumnsToShow: (string | undefined)[];
        };
    };
};

export type PreviewTableResponseType = {
    pinotTableConfig: {
        tableName: string;
        tableType: string;
        segmentsConfig: {
            deletedSegmentsRetentionPeriod: string;
            replication: string;
            segmentPushType: string;
            minimizeDataMovement: boolean;
        };
        tenants: unknown[];
        tableIndexConfig: {
            createInvertedIndexDuringSegmentGeneration: boolean;
            autoGeneratedInvertedIndex: boolean;
            enableDynamicStarTreeCreation: boolean;
            columnMajorSegmentBuilderEnabled: boolean;
            optimizeDictionaryForMetrics: boolean;
            noDictionarySizeRatioThreshold: number;
            rangeIndexVersion: number;
            loadMode: string;
            enableDefaultStarTree: boolean;
            aggregateMetrics: boolean;
            nullHandlingEnabled: boolean;
            optimizeDictionary: boolean;
        };
        metadata: unknown;
        task: {
            taskTypeConfigsMap: {
                FileIngestionTask: {
                    "input.fs.prop.secretKey": string;
                    schedule: string;
                    "input.fs.className": string;
                    "input.fs.prop.region": string;
                    inputDirURI: string;
                    desiredSegmentSize: string;
                    "input.fs.prop.accessKey": string;
                    taskMaxDataSize: string;
                    tableMaxNumTasks: string;
                    inputFormat: string;
                    includeFileNamePattern: string;
                };
            };
        };
        isDimTable: boolean;
    };
    sourceRows: {
        fieldToValueMap: {
            date: string;
            branch_code: string;
            customer_lat: string;
            distance_to_customer_KM: string;
            delivery_fee: string;
            "customerHasloyalty?": string;
            customer_lon: string;
            order_price: string;
            time: string;
            order_id: string;
            order_type: string;
            order_items: string;
        };
        nullValueFields: unknown[];
    }[];
    rows: {
        fieldToValueMap: {
            date: string;
            distance_to_customer_KM: string;
            "customerHasloyalty?": string;
            customer_lon: string;
            order_price: string;
            branch_code: string;
            customer_lat: string;
            delivery_fee: string;
            time: string;
            order_id: string;
            order_type: string;
            order_items: string;
        };
        nullValueFields: unknown[];
    }[];
    summary: {
        nSourceRows: number;
        nSourceColumns: number;
        nRows: number;
        nColumns: number;
        realtime: unknown;
    };
};
